// The public, visibility-filtered view of an event: GET /v1/public/events/:idOrSlug/site.
//
// Consumed by attendee-web, which is unauthenticated and may be read by anyone
// holding a slug. Two properties of this contract are load-bearing and are pinned
// by tests on both sides:
//
//  1. A hidden section's key is ABSENT — never an empty array. `sessions: []`
//     means "nothing scheduled yet"; no `sessions` key at all means "the organiser
//     hid the schedule". A client that cannot tell those apart shows the wrong
//     thing in one of the two cases.
//  2. Every object here is an explicit allowlist. There is no attendee shape in
//     this file, and `.strict()` on PublicSiteSchema means a payload that grew one
//     fails to parse rather than shipping it.
//
// Duplicated in backend/src/schemas/public_site.ts — the backend cannot import
// @event67/shared at build time (design §9.6). backend/tests/contracts.test.ts
// pins the two together.
import { z } from 'zod';
// The coming-soon wording is owned by the event-settings module, which is what
// serves it to the admin panel. Imported rather than redeclared so this page and
// that panel cannot end up describing a hidden section differently.
import { ComingSoonCopySchema } from './event_settings.js';
import { ThemeDocSchema } from './theme.js';
/**
 * The sections an organiser can hide, in display order.
 *
 * Absent from a stored settings document means visible: a section added in a
 * future release is visible with no backfill (design §4.9).
 *
 * `info` is here as a FLAG with no matching array below, and that is not an
 * oversight: this page does not render event info yet. The flag still has to
 * travel, because the registry that writes it and the registry that reads it
 * are pinned to the same key set — a section missing from one side is a flag
 * that is written and never found.
 */
export const SITE_VISIBILITY_SECTIONS = [
    'schedule',
    'speakers',
    'sponsors',
    'info',
    'attendees',
    'photos',
    'announcements',
];
export const SiteVisibilitySectionSchema = z.enum(SITE_VISIBILITY_SECTIONS);
export const SiteVisibilitySchema = z
    .object({
    schedule: z.boolean(),
    speakers: z.boolean(),
    sponsors: z.boolean(),
    info: z.boolean(),
    attendees: z.boolean(),
    photos: z.boolean(),
    announcements: z.boolean(),
})
    .strict();
/**
 * No id and no orgId, deliberately. The caller reached this by slug or uuid and
 * already holds whichever one it used; handing back the other is a free
 * translation between the two for anyone probing the endpoint.
 */
export const PublicSiteEventSchema = z
    .object({
    name: z.string(),
    slug: z.string(),
    startAt: z.string(), // ISO; client-side parsed to Date
    endAt: z.string(),
    timezone: z.string(),
    location: z.string(),
    description: z.string(),
})
    .strict();
export const PublicSiteTrackSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    color: z.string().nullable(),
    sortOrder: z.number().int(),
})
    .strict();
export const PublicSiteSessionSchema = z
    .object({
    id: z.string(),
    trackId: z.string().nullable(),
    type: z.string(),
    title: z.string(),
    description: z.string(),
    startAt: z.string(),
    endAt: z.string(),
    room: z.string().nullable(),
    capacity: z.number().int().nullable(),
    // Present only when BOTH the schedule and the speakers are visible. Hiding
    // the speaker roster while still saying which session has three of them
    // would half-undo the thing the organiser asked for, so the linkage travels
    // with the roster it points into.
    speakerIds: z.array(z.string()).optional(),
})
    .strict();
/**
 * `photoUrl` is an absolute http(s) URL a logged-out browser can fetch, or null.
 * Never a bare Cloud Storage path: attendee-web has no Firebase SDK, so a path
 * renders as nothing. `photoPath` and the raw pasted value behind it stay
 * server-side — see toPublicImageUrl for what survives the filter.
 */
export const PublicSiteSpeakerSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    title: z.string().nullable(),
    company: z.string().nullable(),
    bio: z.string().nullable(),
    photoUrl: z.string().nullable(),
    socialLinks: z.record(z.string()),
})
    .strict();
export const PublicSiteSponsorSchema = z
    .object({
    id: z.string(),
    name: z.string(),
    tier: z.string(),
    description: z.string(),
    logoUrl: z.string().nullable(),
    websiteUrl: z.string().nullable(),
    sortOrder: z.number().int(),
})
    .strict();
/** A sponsor tier heading: the key sponsors carry in `tier`, and what to call it. */
export const PublicSiteSponsorTierSchema = z
    .object({
    key: z.string(),
    label: z.string(),
    sortOrder: z.number().int(),
})
    .strict();
export const PublicSiteSchema = z
    .object({
    event: PublicSiteEventSchema,
    theme: ThemeDocSchema,
    visibility: SiteVisibilitySchema,
    comingSoonCopy: ComingSoonCopySchema,
    // Optional in the schema because a hidden section is an absent key. Present
    // iff the matching visibility flag is true — the backend never emits one of
    // these alongside a false flag, and never emits an empty array in place of a
    // missing key.
    sessions: z.array(PublicSiteSessionSchema).optional(),
    tracks: z.array(PublicSiteTrackSchema).optional(),
    speakers: z.array(PublicSiteSpeakerSchema).optional(),
    sponsors: z.array(PublicSiteSponsorSchema).optional(),
    // The event's sponsor tiers in display order. Present iff `sponsors` is.
    sponsorTiers: z.array(PublicSiteSponsorTierSchema).optional(),
})
    .strict();
export const PublicSiteResponseSchema = z.object({ data: PublicSiteSchema }).strict();
//# sourceMappingURL=public_site.js.map